/**
 * 認証画面の共通部品（family-design「Auth screens」の型）。
 * ログイン・登録・パスワード再設定・セットアップ・メール確認の全画面・全状態で共用する。
 */
import React from 'react'
import { SHORT_LOGO_URL, RAKURIP_MARK_URL } from '../../lib/brandAssets'
import { GenemoFooter } from '../../components/GenemoFooter'

/** 認証画面の外枠: bg 単色・中央にロゴ → カード → 注記。h1 はカード内のタイトル 1 つだけ */
export const AuthShell: React.FC<{
  title: string
  /** カード内に出す一言（title の下） */
  lead?: React.ReactNode
  children: React.ReactNode
  /** カードの外に出す注記（gm-auth-foot） */
  foot?: React.ReactNode
}> = ({ title, lead, children, foot }) => (
  <div className="gm-app gm-auth">
    <div className="gm-auth-inner">
      <div className="gm-auth-brand">
        <img src={SHORT_LOGO_URL} alt="Genemo Short" />
        <p>ショート動画の自動生成</p>
      </div>
      <main className="gm-auth-card">
        <h1>{title}</h1>
        {lead && <p className="m-0 text-center text-[13px] text-ink-muted">{lead}</p>}
        {children}
      </main>
      {foot && <p className="gm-auth-foot">{foot}</p>}
      <GenemoFooter />
    </div>
  </div>
)

/** ラベル付き入力欄。エラーは入力欄の下に「何が違うか・どう直すか」を出す */
export const Field: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; error?: string; hint?: string }
> = ({ id, label, error, hint, className, ...rest }) => {
  const describedBy = [error ? `${id}-error` : '', hint ? `${id}-hint` : ''].filter(Boolean).join(' ') || undefined
  return (
    <div className="gm-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} name={id} className={`gm-input ${className ?? ''}`} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest} />
      {hint && <span id={`${id}-hint`} className="text-xs text-ink-muted">{hint}</span>}
      {error && <span id={`${id}-error`} className="gm-error">{error}</span>}
    </div>
  )
}

/** フォーム全体のエラー・案内（色＋文字。スクリーンリーダーにも通知する） */
export const Notice: React.FC<{ tone: 'bad' | 'ok' | 'info'; children: React.ReactNode }> = ({ tone, children }) => (
  <div
    role={tone === 'bad' ? 'alert' : 'status'}
    className={
      tone === 'bad'
        ? 'rounded-ctl bg-bad-bg px-3 py-2 text-[13px] text-bad'
        : tone === 'ok'
          ? 'rounded-ctl bg-ok-bg px-3 py-2 text-[13px] text-ok'
          : 'rounded-ctl bg-primary-subtle px-3 py-2 text-[13px] text-primary'
    }
  >
    {children}
  </div>
)

/** 送信中のスピナー（ボタン内用。装飾なので読み上げない） */
export const Spinner: React.FC = () => (
  <svg className="h-4 w-4 animate-spin motion-reduce:animate-none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
)

/** Google 公式の G マーク（色・形を変えない） */
export const GoogleMark: React.FC = () => (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
)

/** SSO ボタン2つ（白地・line-strong の枠・公式マーク）。区切りの文言は画面ごとに変えられる */
export const SsoButtons: React.FC<{
  googleHref: string
  rakuripHref: string
  /** ボタン文言の動詞（ログイン / 登録） */
  verb?: string
  orLabel?: string
  highlightGoogle?: boolean
  /** 区切り線をボタンの上に出すか（登録画面はメール欄の上にボタンを置くので下に出す） */
  separator?: 'before' | 'after'
}> = ({ googleHref, rakuripHref, verb = 'ログイン', orLabel = 'または', highlightGoogle, separator = 'before' }) => {
  const sep = <div className="gm-or" role="separator">{orLabel}</div>
  return (
    <>
      {separator === 'before' && sep}
      <div className="grid gap-2">
        <a href={googleHref} className="gm-btn gm-btn-sso" aria-describedby={highlightGoogle ? 'google-hint' : undefined}>
          <GoogleMark />
          Googleで{verb}
        </a>
        {highlightGoogle && <p id="google-hint" className="m-0 text-center text-xs text-ink-muted">このメールアドレスは Google で登録されています。上の「Googleで{verb}」をお使いください。</p>}
        <a href={rakuripHref} className="gm-btn gm-btn-sso">
          <img src={RAKURIP_MARK_URL} alt="" />
          ラクリプで{verb}
        </a>
      </div>
      {separator === 'after' && sep}
    </>
  )
}
